import {
  Box,
  Flex,
  HStack,
  Image,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import mixpanel from "mixpanel-browser";
import { formatCompactUsd, formatPercentApy } from "../../helpers/formatMetrics";
import { normalizeTokenImageUrl } from "../../helpers/tokenImage";

const CARD_BG = "whiteAlpha.100";
const TEXT_MUTED = "whiteAlpha.600";

const CompoundBoostBadge = ({ label }: { label: string }) => {
  const [highlight, ...rest] = label.split(" ");
  const suffix = rest.join(" ");

  return (
    <HStack
      spacing={1.5}
      pl={1.5}
      pr={2.5}
      py={1}
      borderRadius="full"
      bg="linear-gradient(135deg, rgba(34, 197, 94, 0.28) 0%, rgba(16, 185, 129, 0.1) 100%)"
      border="1px solid"
      borderColor="rgba(74, 222, 128, 0.4)"
      boxShadow="0 0 14px rgba(34, 197, 94, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)"
      flexShrink={0}
    >
      <Image src="/jto.svg" alt="" boxSize="16px" borderRadius="full" flexShrink={0} />
      <HStack spacing={1} align="baseline">
        <Text
          fontSize="13px"
          fontWeight="bold"
          color="#bbf7d0"
          letterSpacing="0.04em"
          lineHeight="1"
        >
          {highlight}
        </Text>
        {suffix ? (
          <Text fontSize="13px" fontWeight="semibold" color="#86efac" lineHeight="1">
            {suffix}
          </Text>
        ) : null}
      </HStack>
    </HStack>
  );
};

interface CompoundOpportunityCardProps {
  href: string;
  destinationTracking: string;
  tokenIcons: readonly string[];
  badge?: string;
  title: string;
  protocolName: string;
  protocolLogo?: string;
  strategyType: string;
  tvl: number | null;
  apy: number | null;
  tvlLoading?: boolean;
  apyLoading?: boolean;
}

const OverlappingTokenIcons = ({ icons }: { icons: readonly string[] }) => (
  <Flex position="relative" w="52px" h="28px" flexShrink={0}>
    {icons.length > 1 && (
      <Image
        src={normalizeTokenImageUrl(icons[1])}
        alt=""
        position="absolute"
        left="20px"
        top={0}
        boxSize="28px"
        borderRadius="full"
        objectFit="cover"
      />
    )}
    <Image
      src={normalizeTokenImageUrl(icons[0])}
      alt=""
      position="absolute"
      left={0}
      top={0}
      boxSize="28px"
      borderRadius="full"
      border="2px solid"
      borderColor={CARD_BG}
      objectFit="cover"
      zIndex={1}
    />
  </Flex>
);

const MetricRow = ({
  label,
  value,
  loading,
}: {
  label: string;
  value: string | null;
  loading?: boolean;
}) => (
  <Flex justify="space-between" align="center" gap={2}>
    <Text fontSize="xs" color={TEXT_MUTED} textTransform="uppercase" letterSpacing="0.04em">
      {label}
    </Text>
    {loading ? (
      <Skeleton h="16px" w="56px" borderRadius="md" />
    ) : (
      <Text fontSize="sm" fontWeight="semibold" color="white">
        {value ?? "—"}
      </Text>
    )}
  </Flex>
);

export const CompoundOpportunityCard = ({
  href,
  destinationTracking,
  tokenIcons,
  badge,
  title,
  protocolName,
  protocolLogo,
  strategyType,
  tvl,
  apy,
  tvlLoading = false,
  apyLoading = false,
}: CompoundOpportunityCardProps) => {
  const isExternal = href.startsWith("http");

  return (
    <Box
      as="a"
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      flex="1"
      minW={0}
      p={4}
      borderRadius="8px"
      bg={CARD_BG}
      height="100%"
      transition="all 0.2s"
      _hover={{
        bg: "whiteAlpha.200",
        transform: "translateY(-2px)",
        textDecoration: "none",
        cursor: "pointer",
      }}
      onClick={() => {
        mixpanel.track("Compound Opportunity Clicked", {
          destination: destinationTracking,
        });
      }}
    >
      <VStack align="stretch" spacing={4} height="100%">
        <Flex justify="space-between" align="flex-start" gap={2}>
          <OverlappingTokenIcons icons={tokenIcons} />
          {badge ? <CompoundBoostBadge label={badge} /> : null}
        </Flex>

        <VStack align="stretch" spacing={2} flex={1}>
          <Text fontSize="md" fontWeight="bold" color="white" lineHeight="short">
            {title}
          </Text>
          <HStack spacing={2}>
            {protocolLogo && (
              <Image
                src={normalizeTokenImageUrl(protocolLogo)}
                alt={protocolName}
                boxSize="18px"
                borderRadius="full"
                flexShrink={0}
              />
            )}
            <Text fontSize="sm" fontWeight="medium" color="white">
              {protocolName}
            </Text>
          </HStack>
          <Text fontSize="sm" color={TEXT_MUTED}>
            {strategyType}
          </Text>
        </VStack>

        <VStack align="stretch" spacing={2} pt={1}>
          <MetricRow
            label="TVL"
            value={tvl != null && tvl > 0 ? formatCompactUsd(tvl) : null}
            loading={tvlLoading}
          />
          <MetricRow
            label="APY"
            value={apy != null && apy > 0 ? formatPercentApy(apy) : null}
            loading={apyLoading}
          />
        </VStack>
      </VStack>
    </Box>
  );
};
